// CTV-610: shared playback controller for the background-video editor. Owns the
// preview <video>s and the current playhead position, so the preview player and
// the timeline's scrubber stay in sync: playing advances the playhead; dragging
// the playhead (or clicking the ruler) seeks the video.
//
// DOUBLE-BUFFERED: two stacked <video> elements. One plays (visible) while the
// NEXT clip preloads — seeked to its in-point — in the other (hidden). At a clip
// boundary we just flip which is visible, so there's no src reload and no black
// frame between clips.
//
// HOLD-TO-24s: the loop period is the locked TIMELINE_SEC, matching the render's
// freeze-pad. If the clips total less than that, playback does NOT restart when
// the last clip ends — it holds the last frame (a wall-clock timer advances the
// playhead) until TIMELINE_SEC, then loops.
//
// Imperative + framework-agnostic; React components subscribe via onPosition /
// onPlaying and the controller drives the DOM directly to avoid per-frame
// re-renders.

import { clipAtCum, startAt, totalLength, TIMELINE_SEC, type TimelineClip } from './clipTimeline';

export interface ClipPlayer {
  setClips(clips: TimelineClip[]): void;
  /** Register one of the two stacked preview videos (index 0 or 1). */
  attachVideo(el: HTMLVideoElement | null, index: 0 | 1): void;
  play(): void;
  pause(): void;
  toggle(): void;
  isPlaying(): boolean;
  /** Seek to a cumulative timeline second (keeps the current play/pause state). */
  seek(cumSec: number): void;
  onPosition(cb: (cumSec: number) => void): () => void;
  onPlaying(cb: (playing: boolean) => void): () => void;
}

// Below this remaining slack we treat the track as "full" and loop immediately
// instead of holding a sliver of last frame.
const FULL_EPS = 0.05;

export function createClipPlayer(): ClipPlayer {
  let clips: TimelineClip[] = [];
  const videos: (HTMLVideoElement | null)[] = [null, null];
  const loaded: (string | null)[] = [null, null];
  let active: 0 | 1 = 0; // visible/playing video
  let playing = false;
  let idx = 0; // clip in the ACTIVE video
  let cum = 0; // cumulative timeline position
  let raf: number | null = null;
  // Hold phase (last frame frozen while the playhead runs to TIMELINE_SEC).
  let holding = false;
  let holdStartTs = 0;
  let holdStartCum = 0;
  const posCbs = new Set<(n: number) => void>();
  const playCbs = new Set<(b: boolean) => void>();

  const emitPos = (n: number) => {
    cum = n;
    posCbs.forEach((cb) => cb(n));
  };
  const emitPlaying = () => playCbs.forEach((cb) => cb(playing));
  const stopRaf = () => {
    if (raf != null) cancelAnimationFrame(raf);
    raf = null;
  };
  const tryPlay = (v: HTMLVideoElement | null) => {
    const p = v?.play();
    if (p) p.catch(() => undefined);
  };
  const applyVisibility = () => {
    videos.forEach((v, i) => {
      if (v) {
        v.style.opacity = i === active ? '1' : '0';
        v.style.zIndex = i === active ? '1' : '0';
      }
    });
  };

  const loadInto = (which: 0 | 1, clipIndex: number, localSec: number, autoplay: boolean) => {
    const v = videos[which];
    const clip = clips[clipIndex];
    if (!v || !clip) return;
    if (loaded[which] === clip.previewUrl) {
      try {
        v.currentTime = localSec;
      } catch {
        /* not ready */
      }
      if (autoplay && playing) tryPlay(v);
      return;
    }
    loaded[which] = clip.previewUrl;
    v.src = clip.previewUrl;
    v.onloadedmetadata = () => {
      try {
        v.currentTime = localSec;
      } catch {
        /* ignore */
      }
      if (autoplay && playing) tryPlay(v);
    };
    v.load();
  };

  // Preload clip `i` into the hidden buffer, seeked + paused, for a seamless swap.
  const preloadBuffer = (i: number) => {
    const clip = clips[i];
    if (clip) loadInto((1 - active) as 0 | 1, i, clip.inSec, false);
  };

  // Flip to the pre-buffered video (already showing `next`'s frame) and play it.
  const swapTo = (next: number) => {
    active = (1 - active) as 0 | 1;
    idx = next;
    const nv = videos[active];
    if (nv) {
      try {
        nv.currentTime = clips[idx].inSec;
      } catch {
        /* ignore */
      }
      tryPlay(nv);
    }
    videos[(1 - active) as 0 | 1]?.pause();
    applyVisibility();
  };

  const tick = (ts: number) => {
    if (!playing) return;
    const total = totalLength(clips);

    if (holding) {
      const c = holdStartCum + (ts - holdStartTs) / 1000;
      if (c >= TIMELINE_SEC) {
        holding = false;
        swapTo(0); // buffer was preloaded with clip 0 on hold entry
        emitPos(0);
        preloadBuffer((clips.length > 1 ? 1 : 0));
      } else {
        emitPos(c);
      }
      raf = requestAnimationFrame(tick);
      return;
    }

    const v = videos[active];
    const clip = clips[idx];
    if (v && clip) {
      const windowEnd = clip.inSec + clip.lengthSec;
      if (v.currentTime >= windowEnd - 0.03 || v.ended) {
        const isLast = idx >= clips.length - 1;
        if (isLast && total < TIMELINE_SEC - FULL_EPS) {
          // Hold the last frame until TIMELINE_SEC, then loop.
          holding = true;
          holdStartTs = ts;
          holdStartCum = total;
          v.pause();
          preloadBuffer(0);
          emitPos(total);
        } else {
          const next = (idx + 1) % clips.length;
          swapTo(next);
          emitPos(next === 0 ? 0 : startAt(clips, next));
          preloadBuffer((next + 1) % clips.length);
        }
      } else {
        emitPos(startAt(clips, idx) + (v.currentTime - clip.inSec));
      }
    }
    raf = requestAnimationFrame(tick);
  };

  const startRaf = () => {
    stopRaf();
    raf = requestAnimationFrame(tick);
  };

  const play = () => {
    if (!clips.length || playing) return;
    playing = true;
    emitPlaying();
    const total = totalLength(clips);
    if (cum >= total && total < TIMELINE_SEC - FULL_EPS) {
      // Resume within the hold tail: freeze the last frame, run the clock.
      holding = true;
      holdStartTs = performance.now();
      holdStartCum = cum;
      const at = clipAtCum(clips, total);
      idx = at.index;
      loadInto(active, idx, at.localSec, false);
      preloadBuffer(0);
    } else {
      holding = false;
      const at = clipAtCum(clips, cum);
      idx = at.index;
      loadInto(active, idx, at.localSec, true);
      preloadBuffer((idx + 1) % clips.length);
    }
    applyVisibility();
    startRaf();
  };

  const pause = () => {
    if (!playing) return;
    playing = false;
    emitPlaying();
    videos[active]?.pause();
    stopRaf();
  };

  return {
    setClips(next) {
      clips = next;
      if (idx >= clips.length) idx = 0;
      if (cum > TIMELINE_SEC) emitPos(TIMELINE_SEC);
    },
    attachVideo(el, index) {
      videos[index] = el;
      if (!el) {
        loaded[index] = null;
        if (!videos[0] && !videos[1]) stopRaf();
      } else {
        applyVisibility();
      }
    },
    isPlaying() {
      return playing;
    },
    play,
    pause,
    toggle() {
      if (playing) pause();
      else play();
    },
    seek(cumSec) {
      if (!clips.length) return;
      const total = totalLength(clips);
      const c = Math.max(0, Math.min(cumSec, TIMELINE_SEC));
      const inTail = c >= total && total < TIMELINE_SEC - FULL_EPS;
      const at = clipAtCum(clips, c);
      idx = at.index;
      emitPos(c);
      if (inTail) {
        holding = playing;
        if (playing) {
          holdStartTs = performance.now();
          holdStartCum = c;
        }
        loadInto(active, idx, at.localSec, false); // show last frame, don't autoplay
        preloadBuffer(0);
      } else {
        holding = false;
        loadInto(active, idx, at.localSec, playing);
        preloadBuffer((idx + 1) % clips.length);
      }
      applyVisibility();
    },
    onPosition(cb) {
      posCbs.add(cb);
      return () => posCbs.delete(cb);
    },
    onPlaying(cb) {
      playCbs.add(cb);
      return () => playCbs.delete(cb);
    },
  };
}
